import React from "react";
import { WEARABLE_DATA, WEARABLES } from "@/constants/engines";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/10t7bn5s_gIAjMbPLTXUQHDnFQX0wr.png";

function WearableDataCard({ entry }) {
  return (
    <div className="iw-scan-sweep group flex flex-col overflow-hidden bg-white transition-colors hover:bg-[color:var(--iw-bg)]">
      <div className="relative aspect-[16/10] w-full overflow-hidden border-b border-[color:var(--iw-border)]">
        <img
          src={entry.image}
          alt={entry.title}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(17,41,107,0.0) 35%, rgba(17,41,107,0.55) 100%)" }}
          aria-hidden
        />
        <span className="absolute left-3 top-3 inline-grid h-9 w-9 place-items-center border border-white/40 bg-[color:var(--iw-primary)]/80 text-white backdrop-blur">
          <entry.icon className="h-4 w-4" />
        </span>
      </div>
      <div className="p-5">
        <h4 className="font-display text-lg font-semibold tracking-tight text-[color:var(--iw-primary)]">
          {entry.title}
        </h4>
        <p className="mt-1 text-sm leading-relaxed text-[color:var(--iw-body)]">{entry.body}</p>
      </div>
    </div>
  );
}

function HeroVisual() {
  return (
    <div className="iw-scan-sweep relative overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-primary)]">
      <img
        src={HERO_IMG}
        alt="Wearable streaming sleep, heart rate, recovery and movement data into a connected health dashboard"
        className="block aspect-square w-full object-cover sm:aspect-[4/5] lg:aspect-[4/5]"
        loading="lazy"
        decoding="async"
      />
      {/* Subtle navy tint at the bottom only — keeps the image natural */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2"
        style={{ background: "linear-gradient(to top, rgba(17,41,107,0.55) 0%, rgba(17,41,107,0.0) 100%)" }}
        aria-hidden
      />
      {/* Thin red accent wave near the bottom */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-[88px] h-px"
        style={{ background: "linear-gradient(90deg, transparent 0%, rgba(217,4,41,0.0) 8%, rgba(217,4,41,0.85) 50%, rgba(217,4,41,0.0) 92%, transparent 100%)" }}
        aria-hidden
      />

      {/* Corner brackets */}
      <span className="pointer-events-none absolute left-4 top-4 h-4 w-4 border-l-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
      <span className="pointer-events-none absolute right-4 top-4 h-4 w-4 border-r-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
      <span className="pointer-events-none absolute left-4 bottom-4 h-4 w-4 border-l-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
      <span className="pointer-events-none absolute right-4 bottom-4 h-4 w-4 border-r-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />

      {/* Top-left live tag */}
      <div className="absolute left-4 top-4 inline-flex items-center gap-2 border border-white/40 bg-[color:var(--iw-primary)]/80 px-3 py-1.5 font-mono text-[12px] uppercase tracking-[0.22em] text-white backdrop-blur">
        <span className="iw-pulse-dot block h-1.5 w-1.5 rounded-full bg-[color:var(--iw-accent-red)]" />
        Live Signal
      </div>

      {/* Bottom readout */}
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
        <div className="border-l-2 border-[color:var(--iw-accent-red)] pl-4">
          <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--iw-blue-bright)]">
            Your wrist → Our matrix
          </div>
          <div className="mt-1 font-display text-lg font-semibold leading-tight tracking-tight text-white sm:text-xl">
            Every signal. <span className="text-[color:var(--iw-accent-red)]">Captured.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WearableDataPanel() {
  return (
    <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
      {/* Image hero — left side on desktop */}
      <div className="lg:col-span-5">
        <HeroVisual />
      </div>

      {/* Copy + data grid — right side */}
      <div className="lg:col-span-7">
        <div className="iw-overline mb-3">Wearable data we pull</div>
        <h3 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-3xl lg:text-4xl">
          Your watch already knows.{" "}
          <span className="text-[color:var(--iw-blue)]">We listen.</span>
        </h3>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-[color:var(--iw-body)]">
          Every signal your wearable picks up — sleep, heart, recovery, movement, stress — flows into the Matrix. No new gear. No new habits. We work with what you already wear.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)]">
            Works with
          </span>
          {WEARABLES.map((w) => (
            <span
              key={w}
              className="border border-[color:var(--iw-border)] bg-white px-2.5 py-1 font-mono text-[13px] uppercase tracking-[0.18em] text-[color:var(--iw-primary)]"
            >
              {w}
            </span>
          ))}
        </div>

        <div className="mt-7 grid grid-cols-1 gap-px overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-border)] sm:grid-cols-2 lg:grid-cols-3">
          {WEARABLE_DATA.map((entry) => (
            <WearableDataCard key={entry.title} entry={entry} />
          ))}
        </div>
      </div>
    </div>
  );
}
